import { useEffect, useState } from "react";
import { X, Check, Sliders, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ADJUSTMENT_TYPES, fmtQty } from "../lib";

/**
 * Stock adjustment (Section 21).  Delta may be positive or negative, blocked
 * from making stock go negative on the backend.  Audited via stock_movements.
 */
export default function StockAdjustDialog({ open, onClose, part, onSaved }) {
  const [direction, setDirection] = useState("add"); // add | remove
  const [qty, setQty] = useState(0);
  const [type, setType] = useState("physical_correction");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setDirection("add"); setQty(0); setType("physical_correction");
      setReason(""); setNotes(""); setError(null);
    }
  }, [open]);

  if (!open || !part) return null;

  const newStock = direction === "add"
    ? Number(part.stock_qty || 0) + Number(qty || 0)
    : Number(part.stock_qty || 0) - Number(qty || 0);

  const canSave = Number(qty) > 0 && reason.trim().length > 0;

  const save = async () => {
    if (!canSave) { setError("Enter a positive quantity and a reason"); return; }
    setSaving(true); setError(null);
    try {
      await api.post("/spares/stock-adjustments", {
        part_id: part.id,
        adjustment_type: type,
        delta: direction === "add" ? Number(qty) : -Number(qty),
        reason,
        notes,
      });
      toast.success(`Stock adjusted — ${fmtQty(newStock, part.unit)} now on hand`);
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not adjust stock");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="spare-adjust-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
           data-testid="spare-adjust-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#F7F9FC]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#F79009]/15 text-[#B54708] grid place-items-center">
              <Sliders size={14} />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-[#1d3557]">Adjust stock</h2>
              <p className="text-[11px] text-[#667085] font-mono">{part.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-[#E4E7EC] grid place-items-center text-[#667085]"
                  data-testid="spare-adjust-close">
            <X size={17} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* Direction toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setDirection("add")}
              data-testid="spare-adjust-add"
              className={`h-11 rounded-md text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 ${
                direction === "add" ? "bg-[#12B76A] text-white" : "border border-[#E4E7EC] text-[#667085]"
              }`}
            >
              <Plus size={13} /> Add stock
            </button>
            <button
              type="button"
              onClick={() => setDirection("remove")}
              data-testid="spare-adjust-remove"
              className={`h-11 rounded-md text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 ${
                direction === "remove" ? "bg-[#e63946] text-white" : "border border-[#E4E7EC] text-[#667085]"
              }`}
            >
              <Minus size={13} /> Remove stock
            </button>
          </div>

          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Quantity</div>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="number" value={qty}
                onChange={(e) => setQty(Number(e.target.value) || 0)}
                data-testid="spare-adjust-qty"
                className="flex-1 h-11 px-3 rounded-md border border-[#E4E7EC] text-[16px] font-mono tabular-nums focus:border-[#457b9d] outline-none"
              />
              <span className="text-[11px] text-[#667085] font-mono">{part.unit || ""}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Current</span>
              <div className="mt-1 h-11 px-3 rounded-md border border-[#E4E7EC] bg-[#F7F9FC] text-[15px] font-mono tabular-nums text-[#1d3557] flex items-center">
                {fmtQty(part.stock_qty, part.unit)}
              </div>
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">New</span>
              <div className={`mt-1 h-11 px-3 rounded-md border text-[15px] font-mono tabular-nums font-bold flex items-center ${
                newStock < 0 ? "border-[#e63946] text-[#e63946]" : "border-[#12B76A]/40 text-[#0F9256] bg-[#F1FAEE]"
              }`}
                   data-testid="spare-adjust-new-stock">
                {fmtQty(newStock, part.unit)}
              </div>
            </label>
          </div>

          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Type</span>
            <select value={type} onChange={(e) => setType(e.target.value)}
                    data-testid="spare-adjust-type"
                    className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
              {ADJUSTMENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Reason *</span>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                   data-testid="spare-adjust-reason"
                   placeholder="Damaged during handling / Physical count mismatch"
                   className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
          </label>

          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Notes (optional)</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      data-testid="spare-adjust-notes"
                      className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </label>

          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]"
                 data-testid="spare-adjust-error">
              {error}
            </div>
          )}
        </div>

        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={save} disabled={!canSave || saving}
                  data-testid="spare-adjust-save"
                  className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={13} /> {saving ? "Saving…" : "Save adjustment"}
          </button>
        </footer>
      </div>
    </div>
  );
}
